import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Button,
  Chip,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
const flagLabel = (value, yes, no) =>
  value === true ? yes : value === false ? no : "Sin información";
const hasIntegration = (value) => {
  const text = String(value || "")
    .trim()
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();
  return !!text && !["sin informacion", "sin integracion"].includes(text);
};
export default function TableCommerce({ comercios, totalComercios, onEditClick, disabled }) {
  const [page, setPage] = useState(0),
    [size, setSize] = useState(10);
  useEffect(() => setPage(0), [comercios]);
  return (
    <Paper variant="outlined" sx={{ borderRadius: 3, overflow: "hidden" }}>
      <Box sx={{ p: 2.5, bgcolor: "#eef6fb", borderBottom: "1px solid #d5e5ef" }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#244258" }}>
          Comercios configurados
        </Typography>
        <Typography sx={{ fontSize: 12, color: "#61798d", mt: 0.5 }}>
          {comercios.length} de {totalComercios} comercios
        </Typography>
      </Box>
      <TableContainer>
        <Table
          sx={{
            minWidth: 1000,
            "& th": { bgcolor: "#21546b", color: "#fff", fontSize: 12, fontWeight: 700 },
            "& td": { color: "#29465c", fontSize: 13, borderColor: "#e9eff4" },
            "& tbody tr:nth-of-type(even)": { bgcolor: "#f4f8fc" },
            "& tbody tr.MuiTableRow-hover:hover": { bgcolor: "#e7f2f8" },
          }}
        >
          <TableHead sx={{ display: "table-header-group" }}>
            <TableRow>
              {[
                "ID",
                "Comercio",
                "Estado",
                "Tipo de comercio",
                "Integración",
                "Apertura CB",
                "",
              ].map((label) => (
                <TableCell key={label}>{label}</TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {comercios.slice(page * size, page * size + size).map((row, index) => (
              <TableRow hover key={`${row.id}-${index}`}>
                <TableCell sx={{ fontFamily: "monospace", fontWeight: 600 }}>#{row.id}</TableCell>
                <TableCell>
                  <Typography sx={{ fontSize: 13, fontWeight: 600 }}>
                    {row.nombre || "Sin nombre"}
                  </Typography>
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    variant="outlined"
                    label={flagLabel(row.activo, "Activo", "Inactivo")}
                    sx={{
                      bgcolor:
                        row.activo === true
                          ? "#def3e8"
                          : row.activo === false
                          ? "#fbe7ee"
                          : "#edf1f5",
                      color:
                        row.activo === true
                          ? "#206346"
                          : row.activo === false
                          ? "#963b59"
                          : "#596d80",
                      borderColor: "transparent",
                      fontWeight: 700,
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={row.tipoComercio || "Sin información"}
                    sx={{ bgcolor: "#e4effb", color: "#255b8f", fontWeight: 600 }}
                  />
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={
                      hasIntegration(row.integracion)
                        ? row.integracion
                        : row.tipoIntegracion || "Sin información"
                    }
                    sx={{
                      bgcolor: "#eee9fb",
                      color: "#51479c",
                      fontSize: 13,
                      fontWeight: 700,
                      mb: 0.5,
                    }}
                  />
                  {hasIntegration(row.integracion) && row.tipoIntegracion && (
                    <Typography sx={{ fontSize: 12, color: "#637b8e" }}>
                      {row.tipoIntegracion}
                    </Typography>
                  )}
                </TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    label={flagLabel(row.aperturaCB, "Habilitada", "Deshabilitada")}
                    sx={{
                      bgcolor: row.aperturaCB === true ? "#e5dffb" : "#edf1f5",
                      color: row.aperturaCB === true ? "#51479c" : "#5a7084",
                    }}
                  />
                </TableCell>
                <TableCell>
                  <Button
                    variant="outlined"
                    size="small"
                    disabled={disabled}
                    onClick={() => onEditClick(row)}
                    sx={{
                      "&&": { color: "#245e89", borderColor: "#aac6dc", bgcolor: "#e9f2fb" },
                      "&&:hover": { bgcolor: "#d9eafa" },
                    }}
                  >
                    Editar
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!comercios.length && (
              <TableRow>
                <TableCell colSpan={7} align="center" sx={{ py: 5 }}>
                  No hay comercios que coincidan con los filtros.
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        count={comercios.length}
        page={page}
        rowsPerPage={size}
        onPageChange={(_, value) => setPage(value)}
        onRowsPerPageChange={(e) => {
          setSize(Number(e.target.value));
          setPage(0);
        }}
        rowsPerPageOptions={[10, 25, 50]}
        labelRowsPerPage="Comercios por página"
        labelDisplayedRows={({ from, to, count }) => `${from}–${to} de ${count}`}
      />
    </Paper>
  );
}
TableCommerce.propTypes = {
  comercios: PropTypes.array.isRequired,
  totalComercios: PropTypes.number.isRequired,
  onEditClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
};
